import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { MapPin, Route, User, Clock, Phone, ChevronLeft, ChevronRight, Printer, ArrowUp, ArrowDown, Check, ArrowRight, Store, Map as MapIcon, Sparkles, CloudRain } from "lucide-react";
import { KINDS, todayISO, shiftDays, dayLabel, rangeFor, sameName } from "../reminders/boardUtils";
import { ViewSwitcher } from "../reminders/ViewSwitcher";
import { DayColumns, MonthGrid } from "../reminders/CalendarViews";
import { MagnetPopup } from "../reminders/MagnetPopup";
import RouteMapPopup from "./RouteMapPopup";
import SuggestDayPopup from "../reminders/SuggestDayPopup";
import RainDayPopup from "../reminders/RainDayPopup";
import { printRunSheet } from "../reminders/runsheetPrint";
import { TechAppAdmin } from "./TechAppAdmin";

const skipSunday = (d, dir) => (new Date(d + "T00:00:00").getDay() === 0 ? shiftDays(d, dir) : d);

const JobCard = ({ m, i, last, onMove, onDone, onOpenClient }) => {
  const k = KINDS[m.kind] || KINDS.job;
  return (
    <div data-testid="runsheet-card" className="rounded-2xl border border-slate-200 bg-white p-4 border-l-[6px]" style={{ borderLeftColor: k.color }}>
      <div className="flex items-start gap-3">
        <span className="h-8 w-8 rounded-full flex items-center justify-center font-black text-white shrink-0" style={{ background: k.color }}>{i + 1}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-slate-900">{m.client_full ? `${m.client_full}${m.client_suburb ? ` - ${m.client_suburb}` : ""}` : m.client_name || k.label}</span>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: k.bg, color: k.color }}>{k.label}{m.wo_number ? ` · WO #${m.wo_number}` : ""}</span>
          </div>
          <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{m.text}</p>
          {m.client_address && <a href={`https://maps.google.com/?q=${encodeURIComponent(m.client_address)}`} target="_blank" rel="noreferrer" data-testid="runsheet-map-link" className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-sky-600 hover:underline"><MapPin className="h-4 w-4" />{m.client_address}</a>}
          {m.client_phone && <a href={`tel:${m.client_phone}`} className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Phone className="h-4 w-4" />{m.client_phone}</a>}
        </div>
        {onMove && (
          <div className="flex flex-col gap-1">
            <button disabled={i === 0} onClick={() => onMove(i, -1)} data-testid="runsheet-up-btn" className="rounded border border-slate-200 p-1 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
            <button disabled={last} onClick={() => onMove(i, 1)} data-testid="runsheet-down-btn" className="rounded border border-slate-200 p-1 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        {m.client_id ? (
          onOpenClient && <button onClick={() => onOpenClient(m.client_id)} data-testid="runsheet-open-client-btn" className="flex-1 rounded-full bg-sky-700 hover:bg-sky-800 text-white text-sm font-bold py-2 inline-flex items-center justify-center gap-1">Open Client <ArrowRight className="h-4 w-4" /></button>
        ) : (
          <button onClick={() => onDone(m)} data-testid="runsheet-done-btn" className="flex-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 inline-flex items-center justify-center gap-1"><Check className="h-4 w-4" />Done</button>
        )}
      </div>
    </div>
  );
};

export default function RunSheet({ user, isAdmin, onOpenClient }) {
  const [team, setTeam] = useState([]);
  const [tech, setTech] = useState(user?.name || "");
  const [date, setDate] = useState(skipSunday(todayISO(), 1));
  const [sheet, setSheet] = useState(null);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(date);
  const [cal, setCal] = useState(null);
  const [openMag, setOpenMag] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [suggest, setSuggest] = useState(false);
  const [rain, setRain] = useState(false);
  const range = rangeFor(view, anchor);
  const loadCal = useCallback(async () => {
    try { setCal(await api("GET", `/board?start=${range.start}&days=${range.days}&today=${todayISO()}`)); } catch (e) { toast.error(e.message); }
  }, [range.start, range.days]);
  useEffect(() => { loadCal(); }, [loadCal]);
  const calMags = (cal?.magnets || []).filter((m) => tech && sameName(m.assigned_to, tech));
  const pickDay = (d) => { setDate(d); if (view !== "month") setAnchor(d); };
  const moveDay = async (id, d) => {
    const m = calMags.find((x) => x.id === id);
    if (!m || m.due_date === d) return;
    await api("PATCH", `/reminders/${id}?due_date=${d}`);
    toast.success(`Moved to ${dayLabel(d)}`);
    loadCal(); load();
  };
  const refreshAll = () => { setOpenMag(null); loadCal(); load(); };

  useEffect(() => { api("GET", "/team").then(setTeam).catch(() => setTeam([])); }, []);
  const load = useCallback(async () => {
    if (!tech) { setSheet(null); return; }
    try { setSheet(await api("GET", `/runsheet?tech=${encodeURIComponent(tech)}&date=${date}`)); } catch (e) { toast.error(e.message); }
  }, [tech, date]);
  useEffect(() => { load(); }, [load]);

  const move = async (i, dir) => {
    const list = [...sheet.jobs];
    [list[i], list[i + dir]] = [list[i + dir], list[i]];
    setSheet({ ...sheet, jobs: list });
    try { await Promise.all(list.map((m, n) => api("PATCH", `/reminders/${m.id}?seq=${n}`))); }
    catch (e) { toast.error(`Couldn't save the new order — ${e.message}`); load(); }
  };
  const done = async (m) => { await api("PATCH", `/reminders/${m.id}?done=true`); toast.success("Done — nice work"); load(); loadCal(); };


  return (
    <div data-testid="runsheet-panel">
      <div className="flex items-center gap-2 mb-1"><Route className="h-5 w-5 text-sky-600" /><h1 className="font-display text-2xl font-bold text-slate-900">Technician Run Sheet</h1></div>
      <p className="text-sm text-slate-500 mb-4">Drag a magnet to another day to move it. Tap a day to see its run below (use the arrows to reorder the run).</p>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {isAdmin && (
          <span className="inline-flex items-center gap-2"><User className="h-4 w-4 text-slate-400" />
            <select value={tech} onChange={(e) => setTech(e.target.value)} data-testid="runsheet-tech-select" className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400">
              <option value="">— pick technician —</option>
              {team.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </span>
        )}
        <button onClick={() => setDate(skipSunday(shiftDays(date, -1), -1))} data-testid="runsheet-prev-day" className="rounded-full border border-slate-200 p-2"><ChevronLeft className="h-4 w-4" /></button>
        <span className="font-bold text-slate-800 text-sm min-w-[110px] text-center" data-testid="runsheet-date">{dayLabel(date)}</span>
        <button onClick={() => setDate(skipSunday(shiftDays(date, 1), 1))} data-testid="runsheet-next-day" className="rounded-full border border-slate-200 p-2"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => setSuggest(true)} data-testid="runsheet-suggest-day-btn" className="ml-auto rounded-full border-2 border-sky-300 text-sky-700 hover:bg-sky-50 text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4" />Suggest a Day</button>
        <TechAppAdmin isAdmin={isAdmin} />
        {tech && <button onClick={() => setRain(true)} data-testid="runsheet-rain-day-btn" className="rounded-full border-2 border-sky-700 text-sky-800 hover:bg-sky-50 text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5"><CloudRain className="h-4 w-4" />Rain Day</button>}
        {tech && <button onClick={() => setShowMap(true)} data-testid="runsheet-map-btn" className=" rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5"><MapIcon className="h-4 w-4" />Route Map</button>}
        {tech && <button onClick={() => printRunSheet(tech, date)} data-testid="runsheet-print-btn" className=" rounded-full bg-slate-900 text-white text-sm font-bold px-4 py-2 inline-flex items-center gap-1.5"><Printer className="h-4 w-4" />Print</button>}
      </div>
      {tech && (
        <div className="mb-6 rounded-2xl border-2 border-slate-200 bg-slate-50 p-3" data-testid="runsheet-calendar">
          <div className="mb-3"><ViewSwitcher prefix="runsheet-cal" view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} label={range.label} views={["day", "week", "month"]} /></div>
          {cal && (view === "month"
            ? <MonthGrid prefix="runsheet" dates={cal.dates} month={range.month} today={todayISO()} magnets={calMags} onMove={moveDay} onOpen={setOpenMag} onPickDay={pickDay} selected={date} />
            : <DayColumns dates={cal.dates} magnets={calMags} today={todayISO()} onMove={moveDay} onOpen={setOpenMag} onPickDay={pickDay} selected={date} />)}
        </div>
      )}
      {rain && <RainDayPopup tech={tech} date={date} onClose={() => setRain(false)} onDone={() => { setRain(false); load(); loadCal(); }} />}
      {suggest && <SuggestDayPopup onClose={() => setSuggest(false)} onBooked={() => { setSuggest(false); load(); loadCal(); }} />}
      {showMap && <RouteMapPopup tech={tech} date={date} onClose={() => setShowMap(false)} onReordered={load} />}
      {openMag && <MagnetPopup m={openMag} onClose={() => setOpenMag(null)} onChanged={refreshAll} onOpenClient={onOpenClient || (() => {})} />}
      <div className="max-w-3xl">
      {sheet?.shop_saturday && <div className="mb-3 rounded-xl bg-slate-200 px-4 py-2 text-sm font-bold text-slate-700 inline-flex items-center gap-2" data-testid="runsheet-shop-banner"><Store className="h-4 w-4" />Rostered on Shop / Counter this Saturday</div>}
      {sheet?.away.map((a) => <div key={a.id} className="mb-3 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2 text-sm font-bold text-rose-700">Away / Out: {a.text}</div>)}
      {!sheet || (sheet.jobs.length === 0 && sheet.overdue.length === 0) ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400"><Clock className="h-8 w-8 mx-auto mb-2" /><p className="text-sm">No jobs{tech ? ` for ${tech} on ${dayLabel(date)}` : " — pick a technician"}.</p></div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-3">{sheet.jobs.map((m, i) => <JobCard key={m.id} m={m} i={i} last={i === sheet.jobs.length - 1} onMove={move} onDone={done} onOpenClient={onOpenClient} />)}</div>
          {sheet.overdue.length > 0 && (
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-widest mb-2 text-rose-600">Overdue · {sheet.overdue.length}</p>
              <div className="space-y-3">{sheet.overdue.map((m, i) => <JobCard key={m.id} m={m} i={i} onDone={done} onOpenClient={onOpenClient} />)}</div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
